import { createElement, forwardRef, lazy, useRef } from 'react';
import { NotFound } from './pages/NotFound';
import { itemDetailsPageLoader, itemListPageLoader, profileLoader } from './loaders';

const lazyPage = (importFunc) => {
  const ReactLazyComponent = lazy(importFunc);
  let PreloadedComponent;
  let preloadPromise;

  const Component = forwardRef((props, ref) => {
    const ComponentToRender = useRef(PreloadedComponent || ReactLazyComponent);
    return createElement(ComponentToRender.current, Object.assign(ref ? { ref } : {}, props));
  });
  Component.displayName = 'LazyPage';
  Component.preload = () => {
    if (!preloadPromise) {
      preloadPromise = importFunc().then((module) => {
        PreloadedComponent = module.default;
        Component.displayName = PreloadedComponent.displayName;
        return PreloadedComponent;
      });
    }
    return preloadPromise;
  };
  return Component;
};

/*
TODO: 
- /relatedListTypeParam
- TIME STAMP? https://www.dumpert.nl/?selectedId=100048119_ae1675a7#time=16m53sid=100048119_ae1675a7
- EMBED https://youtu.be/AX4Ysbq3Hus?t=238
DONE:
- datum/
- /:pageNum(\\d+|dag|week|maand|\\d{2}-\\d{2}-\\d{4})
*/

export const routes = [
  {
    path: '/',
    PageComponent: lazyPage(() => import('./lazy/ItemsListPage')),
    adunit: 'homepage',
    loader: itemListPageLoader(
      ({ sorting, filter }) => [
        { list: 'latest', filter },
        { list: 'toppers', filter },
        { list: 'search', sorting: 'date', tag: 'dumperttv' },
      ],
      'toppers',
    ),
  },
  {
    path: '/algemene-voorwaarden',
    PageComponent: lazyPage(() => import('./lazy/AlgemeneVoorwaardenPage')),
  },
  {
    path: '/privacy',
    PageComponent: lazyPage(() => import('./lazy/PrivacyPage')),
  },
  {
    path: '/huisregels',
    PageComponent: lazyPage(() => import('./lazy/HouseRulesPage')),
  },
  {
    path: '/veelgestelde-vragen',
    PageComponent: lazyPage(() => import('./lazy/FaqsPage')),
  },
  {
    path: '/verordening-digitale-diensten',
    PageComponent: lazyPage(() => import('./lazy/DigitalServicesRegulationPage')),
  },
  {
    path: '/contact',
    PageComponent: lazyPage(() => import('./lazy/ContactPage')),
  },
  {
    path: '/toppers/:period?',
    PageComponent: lazyPage(() => import('./lazy/ItemsListPage')),
    adunit: 'toppers',
    loader: itemListPageLoader(
      ({ filter }) => [
        { list: 'toppers', filter },
        { list: 'latest', filter },
        { list: 'search', sorting: 'date', tag: 'dumperttv' },
      ],
      'toppers',
    ),
  },
  {
    path: '/floppers/',
    PageComponent: lazyPage(() => import('./lazy/ItemsListPage')),
    adunit: 'homepage',
    loader: itemListPageLoader(
      ({ filter }) => [
        { list: 'floppers', filter },
        { list: 'toppers', filter },
        { list: 'search', sorting: 'date', tag: 'dumperttv' },
      ],
      'floppers',
    ),
  },
  {
    path: '/dumperttv/',
    PageComponent: lazyPage(() => import('./lazy/ItemsListPage')),
    adunit: 'dumperttv',
    loader: itemListPageLoader(
      ({ sorting, filter }) => [
        { list: 'tag', tag: 'dumperttv', sorting: sorting.search, filter },
        { list: 'toppers', filter },
        { list: 'search', sorting: 'date', tag: 'dumperttv' },
      ],
      'dumperttv',
    ),
  },
  {
    path: '/zoek/:tag',
    PageComponent: lazyPage(() => import('./lazy/ItemsListPage')),
    adunit: 'homepage',
    loader: itemListPageLoader(
      ({ sorting, filter }) => [
        { list: 'search', sorting: sorting.search, filter },
        { list: 'toppers', filter },
        { list: 'search', sorting: 'date', tag: 'dumperttv' },
      ],
      'search',
    ),
  },
  {
    path: '/tag/:tag',
    PageComponent: lazyPage(() => import('./lazy/ItemsListPage')),
    adunit: 'homepage',
    loader: itemListPageLoader(
      ({ sorting, filter }) => [
        { list: 'tag', sorting: sorting.search, filter },
        { list: 'toppers', filter },
        { list: 'search', sorting: 'date', tag: 'dumperttv' },
      ],
      'tag',
    ),
  },
  {
    path: '/datum/:date',
    PageComponent: lazyPage(() => import('./lazy/ItemsListPage')),
    adunit: 'homepage',
    loader: itemListPageLoader(
      ({ filter }) => [
        { list: 'latest', filter },
        { list: 'toppers', filter },
        { list: 'search', sorting: 'date', tag: 'dumperttv' },
      ],
      'datum',
    ),
  },
  {
    path: '/nieuw/:period/:offset?',
    PageComponent: lazyPage(() => import('./lazy/ItemsListPage')),
    adunit: 'homepage',
    loader: itemListPageLoader(
      ({ filter }) => [
        { list: 'latest', filter },
        { list: 'toppers', filter },
        { list: 'search', sorting: 'date', tag: 'dumperttv' },
      ],
      'nieuw',
    ),
  },
  {
    path: '/item/:itemId',
    PageComponent: lazyPage(() => import('./lazy/ItemDetailsPage')),
    adunit: 'detail',
    loader: itemDetailsPageLoader(),
  },

  {
    path: '/embed/:id',
    PageComponent: lazyPage(() => import('./lazy/EmbedPage')),
  },
  {
    path: '/inloggen',
    PageComponent: lazyPage(() => import('./lazy/LoginPage')),
  },
  {
    path: '/inloggenbutcooler',
    PageComponent: lazyPage(() => import('./lazy/LoginPageOTP')),
  },
  {
    path: '/registreren',
    PageComponent: lazyPage(() => import('./lazy/RegisterPage')),
  },
  {
    path: '/stem',
    PageComponent: NotFound,
  },
  {
    path: '/wachtwoord/:type',
    PageComponent: lazyPage(() => import('./lazy/PasswordPage')),
  },
  {
    path: '/email/:type',
    PageComponent: lazyPage(() => import('./lazy/EmailPage')),
  },
  {
    path: '/profiel/:username',
    PageComponent: lazyPage(() => import('./lazy/ProfilePage')),
    protectedPage: true,
    loader: profileLoader(),
  },
  {
    path: '/reaguurder/',
    PageComponent: lazyPage(() => import('./lazy/UserPage')),
    protectedPage: true,
  },
  {
    path: '/reaguurder/instellingen',
    PageComponent: lazyPage(() => import('./lazy/UserSettingsPage')),
    protectedPage: true,
  },
  {
    path: '/reaguurder/afspeellijsten/:userId',
    PageComponent: lazyPage(() => import('./lazy/PlaylistsPage')),
    protectedPage: true,
  },
  {
    path: '/reaguurder/afspeellijst/:userId/:playlistId',
    PageComponent: lazyPage(() => import('./lazy/PlaylistPage')),
    protectedPage: true,
  },
  {
    path: '/favoreeten',
    PageComponent: lazyPage(() => import('./lazy/FavouritesPage')),
    protectedPage: true,
  },
  {
    path: '/afspeellijst/:userId/:playlistId/',
    PageComponent: lazyPage(() => import('./lazy/PlaylistPublicPage')),
    protectedPage: true,
    adunit: 'playlist',
  },
  {
    path: '/afspeellijst/:userId/:playlistId/:itemId',
    PageComponent: lazyPage(() => import('./lazy/PlaylistPublicPage')),
    protectedPage: true,
    adunit: 'playlist',
  },
  {
    path: '/reaguurder/badges',
    PageComponent: lazyPage(() => import('./lazy/BadgesPage')),
    protectedPage: true,
  },
  {
    path: '/particals',
    PageComponent: lazyPage(() => import('./lazy/ParticalsPage')),
    protectedPage: true,
  },
  {
    path: '/statistieken',
    PageComponent: lazyPage(() => import('./lazy/StatsPage')),
    protectedPage: true,
  },

  // not matched path
  { path: '*', PageComponent: NotFound },
];
